'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from '@/context/LanguageContext';
import { LayoutDashboard, Phone, Users, History, Settings, ChevronLeft, ChevronRight, Wifi, PhoneForwarded, ShieldCheck, FlaskConical, GitBranch, Calendar, BarChart2, BookOpen, Bell, MessageCircle, Clock, Ban, Zap, MessageSquare, PhoneCall, Shield, Monitor, Megaphone, ArrowLeftRight, CalendarDays, Timer, Download, Target, BarChart, Grid, History as HistoryIcon, Radio, Voicemail, MessageSquareDot, UserCircle, Video, Layers, Receipt, Megaphone as Campaign, Trophy, Link2, Database, SlidersHorizontal, FileDown, ChevronDown, ChevronUp, Home, Stethoscope, FileText, Building2, Activity, QrCode, PenTool, Brain, Headphones, Hash, ShieldAlert, HeartPulse, AlertTriangle, Briefcase, Upload,  } from 'lucide-react';
import { getActiveMacroSection, type MacroSection } from './TopBar';
import { getAllowedGroups, getHiddenItems } from '@/lib/permissions';

// ─── Role definitions ─────────────────────────────────────────────────────────
// Role logic is now centralised in src/lib/permissions.ts
// (getAllowedGroups / getHiddenItems imported above)

// ─── Nav structure ────────────────────────────────────────────────────────────

interface NavItem {
  key: string;
  labelKey: string;
  href: string;
  icon: React.ElementType;
  badge?: number | null;
}

interface NavGroup {
  id: string;
  labelKey: string;
  icon: React.ElementType;
  items: NavItem[];
  defaultOpen?: boolean;
  /** Which macro-section this group belongs to */
  section: MacroSection;
}

const NAV_GROUPS: NavGroup[] = [
  // ── CHIAMATE ──────────────────────────────────────────────────────────────
  {
    id: 'core',
    labelKey: 'sidebar.groups.core',
    icon: LayoutDashboard,
    defaultOpen: true,
    section: 'chiamate',
    items: [
      { key: 'nav-dashboard', labelKey: 'sidebar.items.dashboard', href: '/call-dashboard', icon: LayoutDashboard },
      { key: 'nav-call-detail', labelKey: 'sidebar.items.callDetail', href: '/call-detail', icon: Phone, badge: 3 },
      { key: 'nav-call-mgmt', labelKey: 'sidebar.items.callMgmt', href: '/call-management', icon: PhoneCall },
      { key: 'nav-history', labelKey: 'sidebar.items.history', href: '/call-history', icon: History },
      { key: 'nav-voicemail', labelKey: 'sidebar.items.voicemail', href: '/voicemail', icon: Voicemail },
      { key: 'nav-callbacks', labelKey: 'sidebar.items.callbacks', href: '/callbacks', icon: Phone },
      { key: 'nav-callbacks-upcoming', labelKey: 'sidebar.items.callbacksUpcoming', href: '/callbacks/upcoming', icon: Clock },
    ],
  },
  {
    id: 'calls-advanced',
    labelKey: 'sidebar.groups.twilioAdvanced',
    icon: Phone,
    defaultOpen: false,
    section: 'chiamate',
    items: [
      { key: 'nav-routing', labelKey: 'sidebar.items.routing', href: '/call-routing', icon: GitBranch },
      { key: 'nav-browser-calling', labelKey: 'sidebar.items.browserCalling', href: '/browser-calling', icon: PhoneCall },
      { key: 'nav-twilio-flex', labelKey: 'sidebar.items.twilioFlex', href: '/twilio-flex', icon: Phone },
      { key: 'nav-call-whisper', labelKey: 'sidebar.items.callWhisper', href: '/call-whisper', icon: Headphones },
      { key: 'nav-voice-intelligence', labelKey: 'sidebar.items.voiceIntelligence', href: '/voice-intelligence', icon: Brain },
      { key: 'nav-call-recordings', labelKey: 'sidebar.items.callRecordings', href: '/call-recordings', icon: HistoryIcon },
    ],
  },
  {
    id: 'followups-group',
    labelKey: 'sidebar.groups.patients',
    icon: PhoneForwarded,
    defaultOpen: false,
    section: 'chiamate',
    items: [
      { key: 'nav-followups', labelKey: 'sidebar.items.followups', href: '/follow-ups', icon: PhoneForwarded },
      { key: 'nav-followup-auto', labelKey: 'sidebar.items.followupAuto', href: '/followup-automation', icon: Zap },
      { key: 'nav-urgent', labelKey: 'sidebar.items.urgent', href: '/urgent', icon: Zap },
    ],
  },

  // ── PAZIENTI ──────────────────────────────────────────────────────────────
  {
    id: 'patients',
    labelKey: 'sidebar.groups.patients',
    icon: Users,
    defaultOpen: true,
    section: 'pazienti',
    items: [
      { key: 'nav-patients', labelKey: 'sidebar.items.patients', href: '/patient-management', icon: Users },
      { key: 'nav-patient-import', labelKey: 'sidebar.items.patientImport', href: '/patient-management/import', icon: Upload },
      { key: 'nav-patient-threads', labelKey: 'sidebar.items.patientThreads', href: '/patient-threads', icon: MessageSquareDot },
      { key: 'nav-referrals', labelKey: 'sidebar.items.referrals', href: '/referrals', icon: Link2 },
      { key: 'nav-blacklist', labelKey: 'sidebar.items.blacklist', href: '/blacklist', icon: Ban },
    ],
  },
  {
    id: 'lab-workflow',
    labelKey: 'sidebar.groups.labWorkflow',
    icon: FlaskConical,
    defaultOpen: false,
    section: 'pazienti',
    items: [
      { key: 'nav-intake', labelKey: 'sidebar.items.intake', href: '/intake', icon: QrCode },
      { key: 'nav-kiosk', labelKey: 'sidebar.items.kiosk', href: '/kiosk', icon: Monitor },
      { key: 'nav-samples', labelKey: 'sidebar.items.samples', href: '/samples', icon: FlaskConical },
      { key: 'nav-results', labelKey: 'sidebar.items.results', href: '/results', icon: FileText },
      { key: 'nav-consents', labelKey: 'sidebar.items.consents', href: '/consents', icon: PenTool },
      { key: 'nav-home-collection', labelKey: 'sidebar.items.homeCollection', href: '/home-collection', icon: Home },
    ],
  },
  {
    id: 'lab-catalog',
    labelKey: 'sidebar.groups.labCatalog',
    icon: BookOpen,
    defaultOpen: false,
    section: 'pazienti',
    items: [
      { key: 'nav-exam-catalog', labelKey: 'sidebar.items.examCatalog', href: '/exam-catalog', icon: BookOpen },
      { key: 'nav-exam-prep', labelKey: 'sidebar.items.examPrep', href: '/exam-prep', icon: FileText },
      { key: 'nav-exams', labelKey: 'sidebar.items.exams', href: '/exams', icon: FlaskConical },
      { key: 'nav-prescriptions', labelKey: 'sidebar.items.prescriptions', href: '/prescriptions', icon: FileText },
    ],
  },
  {
    id: 'agenda',
    labelKey: 'sidebar.groups.agenda',
    icon: Calendar,
    defaultOpen: false,
    section: 'pazienti',
    items: [
      { key: 'nav-calendar', labelKey: 'sidebar.items.calendar', href: '/calendar', icon: Calendar },
      { key: 'nav-cal-agenda', labelKey: 'sidebar.items.calAgenda', href: '/cal-agenda', icon: CalendarDays },
    ],
  },

  // ── OPERATORI ─────────────────────────────────────────────────────────────
  {
    id: 'operators',
    labelKey: 'sidebar.groups.operators',
    icon: UserCircle,
    defaultOpen: true,
    section: 'operatori',
    items: [
      { key: 'nav-operator-dashboard', labelKey: 'sidebar.items.operatorDashboard', href: '/operator-dashboard', icon: UserCircle },
      { key: 'nav-operator-presence', labelKey: 'sidebar.items.operatorPresence', href: '/operator-presence', icon: Activity },
      { key: 'nav-supervisor', labelKey: 'sidebar.items.supervisor', href: '/supervisor', icon: Shield },
      { key: 'nav-wallboard', labelKey: 'sidebar.items.wallboard', href: '/wallboard', icon: Monitor },
      { key: 'nav-leaderboard', labelKey: 'sidebar.items.leaderboard', href: '/operator-leaderboard', icon: Trophy },
      { key: 'nav-comparison', labelKey: 'sidebar.items.comparison', href: '/operator-comparison', icon: BarChart },
    ],
  },
  {
    id: 'shifts-group',
    labelKey: 'sidebar.groups.config',
    icon: Clock,
    defaultOpen: false,
    section: 'operatori',
    items: [
      { key: 'nav-shifts', labelKey: 'sidebar.items.shifts', href: '/shifts', icon: Clock },
      { key: 'nav-shift-swap', labelKey: 'sidebar.items.shiftSwap', href: '/shift-swap', icon: ArrowLeftRight },
      { key: 'nav-worked-hours', labelKey: 'sidebar.items.workedHours', href: '/worked-hours', icon: Timer },
      { key: 'nav-avail-grid', labelKey: 'sidebar.items.availGrid', href: '/availability-grid', icon: Grid },
      { key: 'nav-weekly-avail', labelKey: 'sidebar.items.weeklyAvail', href: '/weekly-availability', icon: CalendarDays },
      { key: 'nav-daily-goals', labelKey: 'sidebar.items.dailyGoals', href: '/daily-goals', icon: Target },
      { key: 'nav-operator-sla', labelKey: 'sidebar.items.operatorSla', href: '/operator-sla', icon: Target },
      { key: 'nav-handover-history', labelKey: 'sidebar.items.handoverHistory', href: '/handover-history', icon: HistoryIcon },
      { key: 'nav-session-replay', labelKey: 'sidebar.items.sessionReplay', href: '/session-replay', icon: Monitor },
    ],
  },
  {
    id: 'communications',
    labelKey: 'sidebar.groups.communications',
    icon: MessageSquare,
    defaultOpen: false,
    section: 'operatori',
    items: [
      { key: 'nav-chat', labelKey: 'sidebar.items.chat', href: '/operator-chat', icon: MessageCircle },
      { key: 'nav-broadcast', labelKey: 'sidebar.items.broadcast', href: '/supervisor-broadcast', icon: Radio },
    ],
  },

  // ── LAB ───────────────────────────────────────────────────────────────────
  {
    id: 'lab-config',
    labelKey: 'sidebar.groups.labConfig',
    icon: SlidersHorizontal,
    defaultOpen: true,
    section: 'lab',
    items: [
      { key: 'nav-lab-profile', labelKey: 'sidebar.items.labProfile', href: '/lab/profile', icon: Building2 },
      { key: 'nav-lab-team', labelKey: 'sidebar.items.labTeam', href: '/lab/team', icon: Users },
      { key: 'nav-lab-roles', labelKey: 'sidebar.items.labRoles', href: '/lab/roles', icon: ShieldCheck },
      { key: 'nav-lab-hours', labelKey: 'sidebar.items.labHours', href: '/lab/hours', icon: Clock },
      { key: 'nav-reminders', labelKey: 'sidebar.items.reminders', href: '/lab/reminders', icon: Bell },
      { key: 'nav-lis', labelKey: 'sidebar.items.lis', href: '/lab/lis', icon: Link2 },
      { key: 'nav-lab-integrations', labelKey: 'sidebar.items.labIntegrations', href: '/lab/integrations', icon: Database },
      { key: 'nav-lab-webhooks', labelKey: 'sidebar.items.labWebhooks', href: '/lab/webhooks', icon: Zap },
      { key: 'nav-lab-api', labelKey: 'sidebar.items.labApi', href: '/lab/api', icon: BookOpen },
      { key: 'nav-lab-sla', labelKey: 'sidebar.items.labSla', href: '/lab/sla', icon: Target },
      { key: 'nav-lab-notifications', labelKey: 'sidebar.items.labNotifications', href: '/lab/notifications', icon: Bell },
      { key: 'nav-sms-templates', labelKey: 'sidebar.items.smsTemplates', href: '/lab/sms-templates', icon: MessageSquareDot },
    ],
  },
  {
    id: 'lab-admin',
    labelKey: 'sidebar.groups.labAdmin',
    icon: Building2,
    defaultOpen: false,
    section: 'lab',
    items: [
      { key: 'nav-billing-dashboard', labelKey: 'sidebar.items.billingDashboard', href: '/billing-dashboard', icon: BarChart2 },
      { key: 'nav-physicians', labelKey: 'sidebar.items.physicians', href: '/physicians', icon: Stethoscope },
      { key: 'nav-billing-advanced', labelKey: 'sidebar.items.billingAdvanced', href: '/billing-advanced', icon: Receipt },
      { key: 'nav-agreements', labelKey: 'sidebar.items.agreements', href: '/agreements', icon: Building2 },
      { key: 'nav-quality', labelKey: 'sidebar.items.quality', href: '/quality', icon: ShieldCheck },
      { key: 'nav-branches', labelKey: 'sidebar.items.branches', href: '/branches', icon: Building2 },
    ],
  },
  {
    id: 'lab-comms',
    labelKey: 'sidebar.groups.communications',
    icon: MessageSquare,
    defaultOpen: false,
    section: 'lab',
    items: [
      { key: 'nav-sms', labelKey: 'sidebar.items.sms', href: '/sms', icon: MessageSquare },
      { key: 'nav-whatsapp', labelKey: 'sidebar.items.whatsapp', href: '/whatsapp-notifications', icon: MessageCircle },
      { key: 'nav-conversations', labelKey: 'sidebar.items.conversations', href: '/conversations', icon: MessageSquareDot },
      { key: 'nav-unified-conv', labelKey: 'sidebar.items.unifiedConv', href: '/unified-conversations', icon: MessageCircle },
      { key: 'nav-video', labelKey: 'sidebar.items.video', href: '/video-consultation', icon: Video },
      { key: 'nav-campaigns', labelKey: 'sidebar.items.campaigns', href: '/campaigns', icon: Campaign },
      { key: 'nav-notify', labelKey: 'sidebar.items.notify', href: '/twilio-notify', icon: Bell },
    ],
  },
  {
    id: 'admin',
    labelKey: 'sidebar.groups.admin',
    icon: ShieldCheck,
    defaultOpen: false,
    section: 'lab',
    items: [
      { key: 'nav-admin', labelKey: 'sidebar.items.admin', href: '/admin', icon: ShieldCheck },
      { key: 'nav-jobs', labelKey: 'sidebar.items.jobs', href: '/admin/jobs', icon: Briefcase },
      { key: 'nav-audit-trail', labelKey: 'sidebar.items.auditTrail', href: '/audit-trail', icon: HistoryIcon },
      { key: 'nav-gdpr', labelKey: 'sidebar.items.gdpr', href: '/gdpr', icon: Shield },
      { key: 'nav-feature-flags', labelKey: 'sidebar.items.featureFlags', href: '/feature-flags', icon: Zap },
      { key: 'nav-api-docs', labelKey: 'sidebar.items.apiDocs', href: '/api-docs', icon: BookOpen },
      { key: 'nav-data-retention', labelKey: 'sidebar.items.dataRetention', href: '/lab/data-retention', icon: Database },
      { key: 'nav-onboarding', labelKey: 'sidebar.items.onboarding', href: '/onboarding', icon: Zap },
      { key: 'nav-scripts', labelKey: 'sidebar.items.scripts', href: '/scripts', icon: BookOpen },
      { key: 'nav-studio', labelKey: 'sidebar.items.studio', href: '/studio-manager', icon: Layers },
      { key: 'nav-announcements', labelKey: 'sidebar.items.announcements', href: '/announcements', icon: Megaphone },
      { key: 'nav-numbers-mgmt', labelKey: 'sidebar.items.numbersMgmt', href: '/numbers-management', icon: Hash },
      { key: 'nav-twilio-health', labelKey: 'sidebar.items.twilioHealth', href: '/twilio-health', icon: HeartPulse },
      { key: 'nav-alert-notifications', labelKey: 'sidebar.items.alertNotifications', href: '/alert-notifications', icon: AlertTriangle },
      { key: 'nav-verify-security', labelKey: 'sidebar.items.verifySecurity', href: '/verify-security', icon: ShieldAlert },
    ],
  },

  // ── ANALYTICS ─────────────────────────────────────────────────────────────
  {
    id: 'analytics',
    labelKey: 'sidebar.groups.analytics',
    icon: BarChart2,
    defaultOpen: true,
    section: 'analytics',
    items: [
      { key: 'nav-analytics', labelKey: 'sidebar.items.analytics', href: '/analytics', icon: BarChart2 },
      { key: 'nav-analytics-telecom', labelKey: 'sidebar.items.analyticsTelecom', href: '/analytics/telecom', icon: Activity },
      { key: 'nav-epidemiology', labelKey: 'sidebar.items.epidemiology', href: '/analytics/epidemiology', icon: Activity },
      { key: 'nav-ai-insights', labelKey: 'sidebar.items.aiInsights', href: '/ai-insights', icon: Brain },
      { key: 'nav-reports', labelKey: 'sidebar.items.reports', href: '/reports', icon: Download },
      { key: 'nav-export', labelKey: 'sidebar.items.export', href: '/export', icon: FileDown },
      { key: 'nav-invoices', labelKey: 'sidebar.items.invoices', href: '/invoices', icon: Receipt },
    ],
  },
];

const BOTTOM_ITEM: NavItem = { key: 'nav-settings', labelKey: 'sidebar.items.settings', href: '/settings', icon: Settings };

// Role badge colors
const ROLE_BADGE_COLORS: Record<string, { bg: string; text: string }> = {
  Receptionist: { bg: 'bg-teal-100',   text: 'text-teal-700' },
  Operatore:   { bg: 'bg-blue-100',   text: 'text-blue-700' },
  Supervisore: { bg: 'bg-purple-100', text: 'text-purple-700' },
  Admin:       { bg: 'bg-amber-100',  text: 'text-amber-700' },
  SuperAdmin:  { bg: 'bg-red-100',    text: 'text-red-700' },
};

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [openGroups, setOpenGroups] = useState<Set<string>>(
    () => new Set(NAV_GROUPS.filter(g => g.defaultOpen).map(g => g.id))
  );
  const pathname = usePathname();
  const { operator } = useAuth();
  const { t } = useTranslation();

  const userRole = operator?.role ?? 'Operatore';
  const allowedGroups = getAllowedGroups(userRole);
  const hiddenItems = getHiddenItems(userRole);

  // Determine active macro-section
  const activeSection = getActiveMacroSection(pathname);

  // Filter groups: only show groups for the active section
  const visibleGroups = NAV_GROUPS
    .filter(g => allowedGroups.includes(g.id))
    .filter(g => !activeSection || g.section === activeSection)
    .map(g => ({
      ...g,
      items: g.items.filter(item => !hiddenItems.includes(item.key)),
    }))
    .filter(g => g.items.length > 0);

  const toggleGroup = (id: string) => {
    setOpenGroups(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    for (const group of visibleGroups) {
      const hasActive = group.items.some(
        item => pathname === item.href || pathname.startsWith(item.href + '/')
      );
      if (hasActive) {
        setOpenGroups(prev => new Set([...prev, group.id]));
      }
    }
  }, [pathname]);

  // When section changes, open defaultOpen groups for that section
  useEffect(() => {
    const defaultIds = NAV_GROUPS
      .filter(g => g.section === activeSection && g.defaultOpen)
      .map(g => g.id);
    if (defaultIds.length > 0) {
      setOpenGroups(prev => new Set([...prev, ...defaultIds]));
    }
  }, [activeSection]);

  const roleBadgeColors = ROLE_BADGE_COLORS[userRole] ?? ROLE_BADGE_COLORS['Operatore'];
  const roleBadgeLabel = t(`sidebar.roles.${userRole}`, userRole);

  return (
    <aside
      aria-label={t('sidebar.ariaLabel')}
      className={`relative flex flex-col bg-white dark:bg-card border-r border-border transition-all duration-300 ease-in-out shrink-0 h-full ${
        collapsed ? 'w-16' : 'w-56'
      }`}
    >
      {/* Logo */}
      <div className={`flex items-center h-14 border-b border-border px-4 ${collapsed ? 'justify-center' : 'gap-3'}`}>
        <div className="flex items-center gap-2.5 shrink-0">
          <AppLogo size={28} />
          {!collapsed && (
            <span className="font-bold text-[14px] text-foreground tracking-tight leading-tight">
              LabCall<span className="text-primary">Manager</span>
            </span>
          )}
        </div>
      </div>

      {/* Role badge */}
      {!collapsed && operator && (
        <div className="mx-3 mt-2.5">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/50 border border-border/60">
            <UserCircle size={12} className="text-muted-foreground shrink-0" aria-hidden="true" />
            <span className="text-[11px] text-muted-foreground truncate flex-1">{operator.name}</span>
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${roleBadgeColors.bg} ${roleBadgeColors.text}`}>
              {roleBadgeLabel}
            </span>
          </div>
        </div>
      )}

      {/* Twilio status */}
      {!collapsed && (
        <div className="mx-3 mt-2">
          <Link
            href="/lab/integrations"
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200/70 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors cursor-pointer"
            role="status"
            aria-label={t('sidebar.twilioAriaLabel')}
            title={t('sidebar.twilioTitle')}
          >
            <Wifi size={11} className="text-green-600 shrink-0" aria-hidden="true" />
            <span className="text-[11px] font-semibold text-green-700 dark:text-green-400 tracking-wide">{t('sidebar.twilioConnected')}</span>
            <Settings size={10} className="ml-auto text-green-600 shrink-0" aria-hidden="true" />
          </Link>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 px-2 py-3 overflow-y-auto scrollbar-thin" aria-label={t('sidebar.mainNav')}>
        {collapsed ? (
          <div className="space-y-0.5">
            {visibleGroups.flatMap(g => g.items).map(item => {
              const ItemIcon = item.icon;
              const label = t(item.labelKey);
              const isActive = pathname === item.href || pathname.startsWith(item.href + '/');
              return (
                <Link
                  key={item.key}
                  href={item.href}
                  title={label}
                  aria-label={label}
                  aria-current={isActive ? 'page' : undefined}
                  className={`group relative flex items-center justify-center px-3 py-2.5 rounded-lg transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 ${
                    isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <ItemIcon size={17} className="shrink-0" aria-hidden="true" />
                  {item.badge != null && (
                    <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-destructive" aria-hidden="true" />
                  )}
                  <span className="absolute left-full ml-2 px-2 py-1 rounded-md bg-foreground text-background text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50" role="tooltip">
                    {label}
                  </span>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="space-y-0.5">
            {visibleGroups.map(group => {
              const GroupIcon = group.icon;
              const isOpen = openGroups.has(group.id);
              const hasActive = group.items.some(
                item => pathname === item.href || pathname.startsWith(item.href + '/')
              );
              const groupLabel = t(group.labelKey);

              return (
                <div key={group.id}>
                  <button
                    onClick={() => toggleGroup(group.id)}
                    aria-expanded={isOpen}
                    aria-controls={`group-${group.id}`}
                    className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                      hasActive
                        ? 'text-primary' : 'text-muted-foreground/70 hover:text-muted-foreground hover:bg-muted/40'
                    }`}
                  >
                    <GroupIcon size={11} aria-hidden="true" />
                    <span className="flex-1 text-left">{groupLabel}</span>
                    {isOpen ? <ChevronUp size={11} aria-hidden="true" /> : <ChevronDown size={11} aria-hidden="true" />}
                  </button>

                  {isOpen && (
                    <div id={`group-${group.id}`} className="mt-0.5 mb-1 space-y-0.5">
                      {group.items.map(item => {
                        const ItemIcon = item.icon;
                        const label = t(item.labelKey);
                        const isActive = pathname === item.href || pathname.startsWith(item.href + '/');
                        return (
                          <Link
                            key={item.key}
                            href={item.href}
                            aria-current={isActive ? 'page' : undefined}
                            className={`group relative flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] font-medium transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 ${
                              isActive
                                ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                            }`}
                          >
                            {isActive && (
                              <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 rounded-r-full bg-primary" aria-hidden="true" />
                            )}
                            <ItemIcon size={15} className="shrink-0" aria-hidden="true" />
                            <span className="flex-1 truncate">{label}</span>
                            {item.badge != null && (
                              <span className="ml-auto min-w-[18px] h-4.5 flex items-center justify-center rounded-full bg-destructive text-white text-[9px] font-bold px-1.5" aria-label={`${item.badge} notifiche`}>
                                {item.badge}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </nav>

      {/* Bottom */}
      <div className="px-2 pb-4 space-y-0.5 border-t border-border pt-3">
        {(() => {
          const item = BOTTOM_ITEM;
          const ItemIcon = item.icon;
          const label = t(item.labelKey);
          const isActive = pathname === item.href;
          return (
            <Link
              key={item.key}
              href={item.href}
              title={collapsed ? label : undefined}
              aria-label={collapsed ? label : undefined}
              className={`group relative flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg text-[13px] font-medium transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 ${collapsed ? 'justify-center' : ''} ${isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
            >
              <ItemIcon size={17} className="shrink-0" aria-hidden="true" />
              {!collapsed && <span>{label}</span>}
              {collapsed && (
                <span className="absolute left-full ml-2 px-2 py-1 rounded-md bg-foreground text-background text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50" role="tooltip">
                  {label}
                </span>
              )}
            </Link>
          );
        })()}
      </div>

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        aria-label={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
        aria-expanded={!collapsed}
        className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-white dark:bg-card border border-border flex items-center justify-center shadow-sm hover:shadow-md hover:bg-muted transition-all z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        {collapsed ? <ChevronRight size={11} aria-hidden="true" /> : <ChevronLeft size={11} aria-hidden="true" />}
      </button>
    </aside>
  );
}